import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CloudService, Ec2Instance, ServiceCardData } from '../../core/services/cloud.service';
import { ToastService } from '../../core/services/toast.service';
import {
  CloudFilters,
  FacetDef,
  SortOption,
  parseFiltersFromParams,
  filtersToParams,
} from './cloud-filter.model';
import { MetricColumn } from './cloud-metrics-panel.component';
import { MonitoringService } from '../../core/services/monitoring.service';
import { ActivityItem, findingsToActivity, sortActivity } from './cloud-activity.model';

type Tab = 'overview' | 'instances' | 'activity';

const HISTORY_METRICS: MetricColumn[] = [
  { key: 'cpu',        label: 'CPU%'     },
  { key: 'statusSys',  label: 'Sys chk'  },
  { key: 'statusInst', label: 'Inst chk' },
];

const DEFAULT_SORT = 'name-asc';
const FACET_KEYS   = ['state', 'monitoring'];

const SORT_OPTIONS: SortOption[] = [
  { value: 'name-asc',        label: 'Name A → Z'      },
  { value: 'name-desc',       label: 'Name Z → A'      },
  { value: 'monitored-first', label: 'Monitored first' },
];

const FACETS: FacetDef[] = [
  {
    key: 'state',
    label: 'State',
    options: [
      { value: 'running',    label: 'Running'    },
      { value: 'stopped',    label: 'Stopped'    },
      { value: 'stopping',   label: 'Stopping'   },
      { value: 'pending',    label: 'Pending'    },
      { value: 'terminated', label: 'Terminated' },
    ],
  },
  {
    key: 'monitoring',
    label: 'Monitoring',
    options: [
      { value: 'monitored',   label: 'Monitored'     },
      { value: 'unmonitored', label: 'Not monitored' },
    ],
  },
];

/**
 * EC2 monitoring is per individual instance — sustained CPU / status-check
 * metrics (CloudWatch InstanceId dimension) plus EventBridge instance
 * state-change events (stopped / terminated). The monitored resource id is the
 * instance id.
 */
@Component({
  selector: 'app-ec2-detail',
  templateUrl: './ec2-detail.component.html',
})
export class Ec2DetailComponent implements OnInit, OnDestroy {
  selectedTab: Tab = 'overview';
  instances: Ec2Instance[] = [];
  loading = true;

  filters: CloudFilters = { query: '', facets: {}, sort: DEFAULT_SORT };

  readonly tabs: { id: Tab; label: string }[] = [
    { id: 'overview',  label: 'Overview'  },
    { id: 'instances', label: 'Instances' },
    { id: 'activity',  label: 'Activity'  },
  ];

  readonly facets      = FACETS;
  readonly sortOptions = SORT_OPTIONS;

  readonly serviceCard: ServiceCardData = { id: 'ec2', name: 'EC2', icon: 'server', route: '/cloud/ec2', status: 'healthy', metrics: [] };
  readonly historyMetrics = HISTORY_METRICS;

  drawerItemId: string | null = null;
  selectedId: string | null = null;

  activity: ActivityItem[] = [];
  activityLoading = true;
  refreshing = false;

  private sub?: Subscription;
  private routeSub?: Subscription;
  private findingsSub?: Subscription;

  constructor(
    private readonly cloud: CloudService,
    private readonly toast: ToastService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly monitoring: MonitoringService,
  ) {}

  ngOnInit(): void {
    this.cloud.loadItemPoliciesForService('ec2').subscribe();
    this.routeSub = this.route.queryParams.subscribe(params => {
      const p = params as Record<string, string>;
      this.filters = parseFiltersFromParams(p, DEFAULT_SORT, FACET_KEYS);
      this.selectedId = p['r'] ?? null;
    });
    this.loadData(false);
  }

  /** Loads the instance list (cache-aware), monitoring state (monitored count), and activity findings. */
  private loadData(bypass: boolean): void {
    this.sub?.unsubscribe();
    this.sub = this.cloud.getEc2Instances(bypass).subscribe(instances => {
      this.instances  = instances;
      this.loading    = false;
      this.refreshing = false;
    });
    this.cloud.loadMonitoringState().subscribe();
    this.findingsSub?.unsubscribe();
    this.findingsSub = this.monitoring.getFindings().subscribe(findings => {
      this.activityLoading = false;
      this.activity        = sortActivity(findingsToActivity(findings, 'EC2_INSTANCE'));
    });
  }

  /** Manual refresh — bypass the cache and reload (guarded against double-clicks). */
  refresh(): void {
    if (this.refreshing) return;
    this.refreshing = true;
    this.loadData(true);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.routeSub?.unsubscribe();
    this.findingsSub?.unsubscribe();
  }

  onActivitySelect(item: ActivityItem): void {
    this.selectedTab = 'instances';
    this.selectResource(item.resourceId);
  }

  // ── Display helpers ─────────────────────────────────────────────────────────

  label(inst: Ec2Instance): string { return inst.name ?? inst.instanceId; }

  // ── Overview counts ─────────────────────────────────────────────────────────

  get totalCount():     number { return this.instances.length; }
  get runningCount():   number { return this.instances.filter(i => i.state === 'running').length; }
  get stoppedCount():   number { return this.instances.filter(i => i.state === 'stopped' || i.state === 'stopping').length; }
  get monitoredCount(): number { return this.instances.filter(i => this.isMonitored(i.instanceId)).length; }

  // ── Filtered + sorted list ──────────────────────────────────────────────────

  get filteredInstances(): Ec2Instance[] {
    let result = this.instances;

    const q = this.filters.query.toLowerCase().trim();
    if (q) result = result.filter(i =>
      i.instanceId.toLowerCase().includes(q) ||
      (i.name ?? '').toLowerCase().includes(q) ||
      i.instanceType.toLowerCase().includes(q),
    );

    const states = this.filters.facets['state'] ?? [];
    if (states.length) result = result.filter(i => states.includes(i.state));

    const monitoring = this.filters.facets['monitoring'] ?? [];
    if (monitoring.length) {
      result = result.filter(i => {
        const mon = this.isMonitored(i.instanceId);
        return (monitoring.includes('monitored') && mon) || (monitoring.includes('unmonitored') && !mon);
      });
    }

    return this.sorted(result, this.filters.sort);
  }

  private sorted(instances: Ec2Instance[], sort: string): Ec2Instance[] {
    return [...instances].sort((a, b) => {
      switch (sort) {
        case 'name-desc':       return this.label(b).localeCompare(this.label(a));
        case 'monitored-first': {
          const aM = this.isMonitored(a.instanceId) ? 0 : 1;
          const bM = this.isMonitored(b.instanceId) ? 0 : 1;
          return aM - bM || this.label(a).localeCompare(this.label(b));
        }
        default: return this.label(a).localeCompare(this.label(b));
      }
    });
  }

  onFiltersChange(f: CloudFilters): void {
    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         filtersToParams(f, FACET_KEYS),
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  // ── Monitoring ──────────────────────────────────────────────────────────────

  get quotaUsage() { return this.cloud.getQuotaUsage(); }
  get canMonitorMore(): boolean { return this.cloud.canEnableMoreMonitoring(); }

  isMonitored(instanceId: string): boolean { return this.cloud.isMonitored('ec2', instanceId); }

  toggleMonitoring(instanceId: string): void {
    const current = this.cloud.isMonitored('ec2', instanceId);
    if (!current && !this.cloud.canEnableMoreMonitoring()) {
      this.toast.show(this.cloud.getQuotaExceededMessage(), 'error');
      return;
    }
    this.cloud.toggleMonitoring('ec2', instanceId, !current).subscribe({
      error: err => {
        if (err.status === 403) {
          const msg = err.error?.code === 'QUOTA_EXCEEDED'
            ? this.cloud.getQuotaExceededMessage()
            : 'Only org owners and admins can configure monitoring.';
          this.toast.show(msg, 'error');
        }
      },
    });
  }

  // ── Metric-history drill-down ───────────────────────────────────────────────

  selectResource(instanceId: string): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { r: instanceId }, queryParamsHandling: 'merge', replaceUrl: true });
  }
  closeResource(): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { r: null }, queryParamsHandling: 'merge', replaceUrl: true });
  }

  // ── Item settings drawer ────────────────────────────────────────────────────

  openItemSettings(instanceId: string): void  { this.drawerItemId = instanceId; }
  closeItemSettings(): void                    { this.drawerItemId = null; }

  itemOverrideSummary(instanceId: string): string {
    return this.cloud.getItemOverrideSummary('ec2', instanceId);
  }

  // ── Status badge ────────────────────────────────────────────────────────────

  statusClass(state: string): string {
    if (state === 'running')                          return 'bg-emerald-100 text-emerald-700';
    if (state === 'stopped')                          return 'bg-slate-100 text-slate-500';
    if (state === 'terminated' || state === 'shutting-down') return 'bg-red-100 text-red-700';
    return 'bg-amber-100 text-amber-700'; // pending / stopping — transitional
  }
}
